import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Input, Button, Alert, Switch, message, Space, Spin } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import styled from "styled-components";
import customClientAxios from "../customAxios";
import * as Api from "../utils/ApiEndpoints";

const PageWrapper = styled.div`padding: 28px 32px; max-width: 800px;`;
const Header = styled.div`display: flex; align-items: center; gap: 12px; margin-bottom: 24px;`;
const Title = styled.h1`font-size: 24px; font-weight: 600; color: #1e293b; margin: 0;`;
const Card = styled.div`
  background: white; border: 1px solid #e2e8f0; border-radius: 16px;
  padding: 28px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;
const BannerPreview = styled.div`
  margin-top: 12px; height: 160px; border-radius: 12px;
  background: ${(p) => (p.$src ? `url(${p.$src}) center/cover no-repeat` : "#f1f5f9")};
  border: 1px dashed #cbd5e1;
  display: flex; align-items: center; justify-content: center; color: #94a3b8; font-size: 13px;
`;

const slugify = (s) => String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function CategoryPageForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);

  useEffect(() => {
    if (mode !== "edit") return;
    (async () => {
      try {
        const res = await customClientAxios().get(Api.CategoryPageDetail.replace(":id", id));
        const d = res.data?.data;
        form.setFieldsValue({
          name: d.name,
          slug: d.slug,
          description: d.description,
          bannerImage: d.bannerImage,
          published: d.status === "published",
        });
        setBannerPreview(d.bannerImage || "");
        setSlugDirty(true);
      } catch (e) {
        setError(e?.response?.data?.message || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, id, form]);

  const onNameChange = (e) => {
    if (!slugDirty) form.setFieldValue("slug", slugify(e.target.value));
  };

  const onSubmit = async (values) => {
    setSaving(true);
    setError(null);
    const payload = {
      name: values.name,
      slug: slugify(values.slug),
      description: values.description || "",
      bannerImage: values.bannerImage || "",
      status: values.published ? "published" : "draft",
    };
    try {
      if (mode === "edit") {
        await customClientAxios().patch(Api.CategoryPageDetail.replace(":id", id), payload);
        message.success("Saved");
        navigate(`/category-pages/${id}`);
      } else {
        const res = await customClientAxios().post(Api.CategoryPagesList, payload);
        message.success("Created");
        navigate(`/category-pages/${res.data?.data?._id}`);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageWrapper><Spin /></PageWrapper>;

  return (
    <PageWrapper>
      <Header>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/category-pages")}>Back</Button>
        <Title>{mode === "edit" ? "Edit Category Page" : "New Category Page"}</Title>
      </Header>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Card>
        <Form form={form} layout="vertical" onFinish={onSubmit} initialValues={{ published: false }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Wellness Retreats" onChange={onNameChange} size="large" />
          </Form.Item>

          <Form.Item
            label="Slug"
            name="slug"
            tooltip="URL path: growezy.club/category/<slug>"
            rules={[{ required: true, message: "Required" }, { pattern: /^[a-z0-9-]+$/, message: "Lowercase letters, numbers, hyphens only" }]}
          >
            <Input addonBefore="/category/" onChange={() => setSlugDirty(true)} />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={4} placeholder="Short description shown under the banner." />
          </Form.Item>

          <Form.Item label="Banner Image URL" name="bannerImage" tooltip="Public image URL (16:9 recommended).">
            <Input placeholder="https://media.growezy.club/..." onChange={(e) => setBannerPreview(e.target.value)} />
          </Form.Item>
          <BannerPreview $src={bannerPreview}>
            {!bannerPreview && "Banner preview"}
          </BannerPreview>

          <Form.Item label="Published" name="published" valuePropName="checked" style={{ marginTop: 24 }}>
            <Switch />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" loading={saving} size="large">
              {mode === "edit" ? "Save changes" : "Create category page"}
            </Button>
            <Button onClick={() => navigate("/category-pages")} size="large">Cancel</Button>
          </Space>
        </Form>
      </Card>
    </PageWrapper>
  );
}
